import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "./assets/styles/global.scss";

import { FingerprintProvider } from "@fingerprint/react";
import * as FullStory from "@fullstory/browser";
import loadable from "@loadable/component";
import { Suspense, useEffect } from "react";
import ReactDOM from "react-dom";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";

import BigFlipLoading from "src/components/BigFlipLoading";
import { ErrorFallback } from "./components";
import WebRouter from "./routes";
const ChatWidget = loadable(() => import("src/components/ChatWidget"));
const VersionUpdateBanner = loadable(() => import("src/components/VersionUpdateBanner"));

import browserHistory from "src/helpers/browser-history";
import FeaturesToggleProvider from "./contexts/FeaturesToggle";
import TimerProvider from "./contexts/Timer";
import { initializeAnalytics } from "./helpers/amplitude/analytics-helpers";
import { initSentry } from "./helpers/sentry";
import "./i18n";
import store from "./services/store";
import { loadNexus } from "./helpers/nexus/refetch-controller";
import { useNexusLiveRefetch } from "./hooks/useNexus/useNexusLiveRefetch";
import eventBus from "./helpers/eventBus";
import { useVersionCheck } from "./hooks/useVersionCheck";

try {
  initializeAnalytics();
} catch (e) {
  console.error("[init] Analytics failed to initialize: ", e);
}

const apiKeyFingerPrint = process.env.BIG_FLIP_FINGERPRINT_API_KEY;
const endpointFingerPrint = process.env.BIG_FLIP_FINGERPRINT_ENDPOINT;

const App = () => {
  const { hasNewVersion, reloadApp } = useVersionCheck();

  // Post-auth Nexus: initial anon load, then reload with the company-specific id after login.
  // loadNexus serializes both via a request token so a late "ffb" response can't clobber company data.
  useEffect(() => {
    loadNexus("ffb");
    const unsubscribe = eventBus.on("user-data-fetched", (data) => {
      loadNexus(data?.company?.company_id || "ffb");
    });
    return unsubscribe;
  }, []);

  // Keep variants fresh mid-session (rollout/rollback) without a full reload.
  useNexusLiveRefetch();

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <FingerprintProvider
        apiKey={apiKeyFingerPrint}
        endpoints={endpointFingerPrint}
        cache={{ storage: "sessionStorage", duration: 3600 }}
      >
        <Suspense fallback={<BigFlipLoading />}>
          {hasNewVersion && <VersionUpdateBanner onReload={reloadApp} />}
          <TimerProvider>
            <Provider store={store}>
              <FeaturesToggleProvider>
                <HelmetProvider>
                  <Router history={browserHistory}>
                    <WebRouter />
                  </Router>
                </HelmetProvider>
                <ChatWidget />
              </FeaturesToggleProvider>
            </Provider>
          </TimerProvider>
        </Suspense>
      </FingerprintProvider>
    </ErrorBoundary>
  );
};

if (process.env.NODE_ENV === "production") {
  try {
    TagManager.initialize({ gtmId: process.env.BIG_FLIP_GTM_ID });
  } catch (e) {
    console.error("[init] Google Tag Manager failed to initialize: ", e);
  }

  try {
    initSentry();
  } catch (e) {
    console.error("[init] Sentry failed to initialize: ", e);
  }

  try {
    if (process.env.BIG_FLIP_ORGANIZATION) {
      FullStory.init({ orgId: process.env.BIG_FLIP_ORGANIZATION });
    }
  } catch (e) {
    console.error("[init] FullStory failed to initialize: ", e);
  }
}

// Render Flip for Business
ReactDOM.render(<App />, document.getElementById("root"));
